type PriceSource = {
  price?: string | number | null;
  offer_price?: string | number | null;
  size_chart?: unknown;
};

const toNumberOrNull = (value: unknown) => (
  value !== "" && value != null && !Number.isNaN(Number(value)) ? Number(value) : null
);

const normalizePriceKey = (key: string) => key.toLowerCase().replace(/[^a-z0-9]/g, "");

const getRowPriceByKeys = (row: Record<string, unknown>, keys: string[]) => {
  const keySet = new Set(keys);
  for (const [key, value] of Object.entries(row)) {
    if (!keySet.has(normalizePriceKey(key))) continue;
    const numericValue = toNumberOrNull(value);
    if (numericValue != null) return numericValue;
  }

  return null;
};

export const getSizeChartRowLabel = (row: Record<string, unknown>) => {
  const direct = String(row.label || "").trim();
  if (direct) return direct.replace(/^label\s*:\s*/i, "").trim();

  const labelledValue = Object.values(row).find((value) => /^label\s*:/i.test(String(value || "").trim()));
  return labelledValue ? String(labelledValue).replace(/^label\s*:\s*/i, "").trim() : "";
};

const getRowOfferPrice = (row: Record<string, unknown> | null | undefined) => {
  if (!row) return null;

  return getRowPriceByKeys(row, ["offerprice", "saleprice", "sellingprice", "discountprice", "discountedprice"]);
};

const getRowBasePrice = (row: Record<string, unknown> | null | undefined) => {
  if (!row) return null;

  return getRowPriceByKeys(row, ["price", "regularprice"]);
};

export const getSizeChartRows = (product: PriceSource) => (
  Array.isArray(product.size_chart) ? product.size_chart as Record<string, unknown>[] : []
);

export const getDefaultSizeRow = (product: PriceSource) => {
  const rows = getSizeChartRows(product);
  return rows.find((row) => row && /\bM\b|medium/i.test(getSizeChartRowLabel(row))) || rows[0] || null;
};

export const getDefaultSizeLabel = (product: PriceSource) => {
  const row = getDefaultSizeRow(product);
  return row ? getSizeChartRowLabel(row) : "";
};

export const getSelectedSizeOfferPrice = (product: PriceSource, selectedSize?: string | null) => {
  const rows = getSizeChartRows(product);
  const selectedRow = selectedSize
    ? rows.find((row) => row && getSizeChartRowLabel(row) === String(selectedSize).trim())
    : null;
  const row = selectedRow || getDefaultSizeRow(product);
  const rowOffer = getRowOfferPrice(row);
  const rowBase = getRowBasePrice(row);

  return rowOffer ?? toNumberOrNull(product.offer_price) ?? rowBase ?? toNumberOrNull(product.price) ?? 0;
};

export const getDefaultSizeOfferPrice = (product: PriceSource) => getSelectedSizeOfferPrice(product);

export const getSelectedSizeRegularPrice = (product: PriceSource, selectedSize?: string | null) => {
  const rows = getSizeChartRows(product);
  const selectedRow = selectedSize
    ? rows.find((row) => row && getSizeChartRowLabel(row) === String(selectedSize).trim())
    : null;
  const row = selectedRow || getDefaultSizeRow(product);
  const rowBase = getRowBasePrice(row);
  return rowBase ?? toNumberOrNull(product.price) ?? 0;
};
